// apps/spa/src/components/commands/DeleteCommandDialog.tsx
// CMD-03 — AlertDialog for slash command delete confirmation.
//
// Per CONTEXT spec:
//   Title: 'Delete slash command?'
//   Body:  '"/<commandName>" will be permanently removed and deregistered from
//           Discord. The audit log preserves a record.'
//   Buttons: [ Cancel ] + [ Delete ] (destructive)
//
// On confirm: useDeleteCommand().mutate(command.id).
// Toast is fired from useDeleteCommand's onSuccess/onError handlers.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeleteCommand } from '@/hooks/useCommands';
import type { SlashCommand } from '@guardian/shared';

export interface DeleteCommandDialogProps {
  command: SlashCommand | null;
  open: boolean;
  onClose: () => void;
}

export function DeleteCommandDialog({
  command,
  open,
  onClose,
}: DeleteCommandDialogProps) {
  const del = useDeleteCommand();

  function handleConfirm() {
    if (!command) return;
    del.mutate(command.id, {
      onSuccess: () => onClose(),
      onError: () => onClose(),
    });
  }

  // Render even when command is null so the dialog can animate closed gracefully.
  return (
    <AlertDialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete slash command?</AlertDialogTitle>
          <AlertDialogDescription>
            {command
              ? `"/${command.name}" will be permanently removed and deregistered from Discord. The audit log preserves a record.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>[ Cancel ]</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={del.isPending}
            onClick={handleConfirm}
          >
            [ Delete ]
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
